import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { IonicModule, AlertController, LoadingController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule } from '@swimlane/ngx-datatable';
import moment from 'moment';

import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import { FilterOption, SelectedFilterMap } from 'src/app/utils/filter-utils';
import { getWhereClausesFromFilters } from 'src/app/utils/utils';
import { createDataSource, FetchDataRequest, FetchDataResponse } from 'src/directives/paginated-datatable.directive';
import { SharedDirectivesModule } from 'src/directives/shared-directives.module';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';

interface ConversationDetails {
  id: string;
  friendlyId: number;
  title: string | null;
  summary: string;
}

interface KnowledgeGapTopic {
  id: string;
  conversationId: string;
  topicTitle: string;
  topicDescription: string;
  context: string;
  confidence: number;
  createdAt: string;
  citedArticleId?: string;
  conversation: ConversationDetails;
}

interface InsightAction {
  type: 'create_new' | 'update_existing';
  description: string;
  topics: string[];
  signalType: 'citation_failure' | 'knowledge_gap';
  index: number;
  isDismissed: boolean;
}

interface KnowledgeGapInsight {
  id: string;
  tenantId: string;
  projectId: string;
  category: string;
  language: string;
  topics: KnowledgeGapTopic[];
  actions: InsightAction[];
  createdAt: string;
  updatedAt: string;
}

interface KnowledgeGapInsightsResponse {
  data: KnowledgeGapInsight[];
  cursors: {
    next?: string;
    previous?: string;
    hasNext: boolean;
    hasPrevious: boolean;
  };
  totalCount?: number;
}

@Component({
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxDatatableModule,
    SharedDirectivesModule,
    SharedPipesModule,
    RouterModule,
    FilterButtonComponent,
  ],
  selector: 'app-knowledge-gap-insights',
  templateUrl: './knowledge-gap-insights.component.html',
  styleUrls: ['./knowledge-gap-insights.component.scss'],
})
export class KnowledgeGapInsightsComponent implements OnInit {
  isLoading = true;
  ColumnMode = ColumnMode;

  limit = 10;
  cursor: any;
  searchText?: string;
  insights: KnowledgeGapInsight[] = [];

  sortField = 'createdAt';
  sortDesc = true;
  sortLabel = 'Newest';

  filters: SelectedFilterMap = new Map();
  filterOptions: FilterOption[] = [
    {
      label: 'Project',
      id: 'projectId',
      icon: 'folder-open',
      type: 'select',
    },
    {
      label: 'Show dismissed actions',
      id: 'showDismissed',
      icon: 'eye-off',
      type: 'select',
      values: [
        { label: 'Yes', id: 'true' },
        { label: 'No', id: 'false' },
      ],
    },
  ];

  fetchInsights = async ({
    limit = this.limit,
    order = [this.sortField],
    descending = this.sortDesc,
    cursor,
  }: FetchDataRequest = {}): Promise<FetchDataResponse<KnowledgeGapInsight>> => {
    const options: any = {
      limit,
      order,
      descending,
      cursor,
      search: this.searchText || undefined,
    };

    const where = this.getWhereClauses();
    if (where.length) {
      options.where = where;
    }

    // Add pagination parameters
    const params: any = {
      limit,
      descending: descending ? 'true' : 'false',
      cursor,
    };

    // Add search text
    if (this.searchText && this.searchText.trim()) {
      params.search = this.searchText.trim();
    }

    // Always respect the selected project from project selector
    if (this.authService.selectedProject) {
      params.projectId = this.authService.selectedProject.id;
    } else {
      // Only use filter projectId if no project is selected in selector
      const projectFilter = this.filters.get(this.filterOptions.find((f) => f.id === 'projectId'));
      if (projectFilter && projectFilter.length > 0) {
        params.projectId = projectFilter[0].id;
      }
    }

    // Process other filters
    this.filters.forEach((values, filterOption) => {
      if (values && values.length > 0) {
        const selectedValue = values[0].id;

        switch (filterOption.id) {
          case 'projectId':
            // Already handled above
            break;
          case 'showDismissed':
            if (selectedValue === 'true') {
              params.showDismissed = true;
            }
            break;
        }
      }
    });

    const result = (await this.apiService.postPromise(
      `/knowledge-gaps/insights`,
      params,
    )) as KnowledgeGapInsightsResponse;

    return {
      data: result.data || [],
      totalCount: result.totalCount || 0,
      cursors: {
        hasNext: result.cursors?.hasNext || false,
        hasPrevious: result.cursors?.hasPrevious || false,
        next: result.cursors?.next,
        previous: result.cursors?.previous,
      },
    };
  };

  insightsDataSource = createDataSource(this.fetchInsights);

  constructor(
    public authService: AuthService,
    private apiService: ApiService,
    private alertController: AlertController,
    private loadingController: LoadingController,
    private messageService: MessageService,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.getInsightsInitial();
  }

  async getInsightsInitial() {
    this.isLoading = true;
    try {
      await this.insightsDataSource.firstPage();
      this.updateFilterOptions();
    } finally {
      this.isLoading = false;
    }
  }

  getWhereClauses() {
    const filterWheres = getWhereClausesFromFilters(this.filters);

    if (this.authService.selectedProject) {
      filterWheres.push({
        field: 'projectId',
        operator: '==',
        value: this.authService.selectedProject.id,
      });
    }

    return filterWheres;
  }

  async getInsights() {
    this.isLoading = true;
    try {
      await this.insightsDataSource.firstPage();
      this.updateFilterOptions();
    } finally {
      this.isLoading = false;
    }
  }

  updateFilterOptions() {
    // Project filter options could be populated from authService.projects if needed
    // For now, keeping it simple since users can already filter by selected project
  }

  filtersDidChange(selected: SelectedFilterMap) {
    this.filters = selected;
    this.getInsights();
  }

  onSearch() {
    this.getInsights();
  }

  displayDate(date: string) {
    return moment(date).fromNow();
  }

  getConfidenceColor(score: number): string {
    if (score >= 0.8) return 'success';
    if (score >= 0.6) return 'warning';
    return 'danger';
  }

  getRelatedTopicsDisplay(topics: string[]): string {
    if (!topics || topics.length === 0) return '';
    if (topics.length <= 2) return topics.join(', ');
    return `${topics.slice(0, 2).join(', ')} +${topics.length - 2} more`;
  }

  getConversationUrl(topic: string): string {
    // Simple implementation - navigate to conversations with a search for the topic
    const encodedTopic = encodeURIComponent(topic);
    const baseUrl = window.location.origin;
    return `${baseUrl}/dashboard/conversations?search=${encodedTopic}`;
  }

  getConversationUrlByTopic(topic: KnowledgeGapTopic): string {
    // Navigate directly to the conversation using conversationId
    const baseUrl = window.location.origin;
    return `${baseUrl}/dashboard/conversations/${topic.conversationId}`;
  }

  getConversationUrlForCategory(category: string): string {
    // Navigate to conversations page with search for the category
    const encodedCategory = encodeURIComponent(category);
    const baseUrl = window.location.origin;
    return `${baseUrl}/dashboard/conversations?search=${encodedCategory}`;
  }

  getTotalConversationCount(insight: KnowledgeGapInsight): number {
    // Count unique conversations from topics
    if (!insight.topics) return 0;
    const uniqueConversationIds = new Set(insight.topics.map((topic) => topic.conversationId));
    return uniqueConversationIds.size;
  }

  trackByInsight(index: number, insight: KnowledgeGapInsight): string {
    return insight.id;
  }

  async nextPage() {
    this.isLoading = true;
    try {
      await this.insightsDataSource.nextPage();
    } finally {
      this.isLoading = false;
    }
  }

  async previousPage() {
    this.isLoading = true;
    try {
      await this.insightsDataSource.previousPage();
    } finally {
      this.isLoading = false;
    }
  }

  getConversationTitle(topic: KnowledgeGapTopic): string {
    // Use conversation title if available, otherwise fall back to topic title
    const title = topic.conversation.title || topic.topicTitle || 'Conversation';
    return `#${topic.conversation.friendlyId} - ${title}`;
  }

  getActionButtonText(action: InsightAction): string {
    switch (action.type) {
      case 'create_new':
        return 'Generate Article';
      case 'update_existing':
        return 'Update Article';
      default:
        return 'Execute Action';
    }
  }

  hasCreateNewAction(insight: KnowledgeGapInsight): boolean {
    return insight.actions?.some((action) => action.type === 'create_new' && !action.isDismissed) || false;
  }

  getCreateNewAction(insight: KnowledgeGapInsight): InsightAction | undefined {
    return insight.actions?.find((action) => action.type === 'create_new');
  }

  // Track which actions have their conversations expanded
  expandedActionMap = new Map<string, boolean>();

  toggleActionConversations(insightId: string, actionIndex: number) {
    const key = `${insightId}-${actionIndex}`;
    this.expandedActionMap.set(key, !this.expandedActionMap.get(key));
  }

  isActionExpanded(insightId: string, actionIndex: number): boolean {
    return this.expandedActionMap.get(`${insightId}-${actionIndex}`) || false;
  }

  getTopicsForAction(insight: KnowledgeGapInsight, action: InsightAction): KnowledgeGapTopic[] {
    if (!insight.topics || !action.topics) return [];
    return insight.topics.filter((topic) => action.topics.includes(topic.id));
  }

  getNonDismissedActions(insight: KnowledgeGapInsight): InsightAction[] {
    // Check if "Show dismissed actions" filter is enabled
    const showDismissedFilter = this.filters.get(this.filterOptions.find((f) => f.id === 'showDismissed'));
    const showDismissedActions =
      showDismissedFilter && showDismissedFilter.length > 0 && showDismissedFilter[0].id === 'true';

    if (showDismissedActions) {
      return insight.actions || [];
    }

    return insight.actions?.filter((action) => !action.isDismissed) || [];
  }

  async executeAction(insight: KnowledgeGapInsight, action: InsightAction) {
    // Get the first topic for this action
    const topics = this.getTopicsForAction(insight, action);
    if (topics.length === 0) {
      this.messageService.showError('No topics found for this action');
      return;
    }

    // Determine the route based on action type
    if (action.type === 'update_existing') {
      // For updates, we need to get the target article ID from the first topic
      const firstTopic = topics[0];
      if (!firstTopic.citedArticleId) {
        this.messageService.showError('No target article found for update action');
        return;
      }

      // Navigate to the existing article's edit page
      this.router.navigate(['../articles', firstTopic.citedArticleId, 'edit'], {
        relativeTo: this.route,
        queryParams: {
          topicId: firstTopic.id,
          from: 'insights',
        },
      });
    } else {
      // For create_new, navigate to the new article page
      this.router.navigate(['../articles/new'], {
        relativeTo: this.route,
        queryParams: {
          topicId: topics[0].id,
          from: 'insights',
        },
      });
    }
  }

  async dismissAction(insight: KnowledgeGapInsight, action: InsightAction) {
    const topics = this.getTopicsForAction(insight, action);
    if (topics.length === 0) return;

    const loading = await this.loadingController.create({
      message: 'Dismissing action...',
    });
    await loading.present();

    try {
      await this.apiService.postPromise(`/knowledge-gaps/insights/${insight.id}/actions/dismiss`, {
        actionType: action.type,
        topicId: topics[0].id,
      });

      await loading.dismiss();
      this.messageService.show('Action dismissed successfully.');
      await this.insightsDataSource.reloadPage();
    } catch (error: any) {
      await loading.dismiss();
      this.messageService.showError(error);
    }
  }

  async undismissAction(insight: KnowledgeGapInsight, action: InsightAction) {
    const topics = this.getTopicsForAction(insight, action);
    if (topics.length === 0) return;

    const loading = await this.loadingController.create({
      message: 'Restoring action...',
    });
    await loading.present();

    try {
      await this.apiService.deletePromise(`/knowledge-gaps/insights/${insight.id}/actions/dismiss`, undefined, {
        actionType: action.type,
        topicId: topics[0].id,
      });

      await loading.dismiss();
      this.messageService.show('Action restored successfully.');
      await this.insightsDataSource.reloadPage();
    } catch (error: any) {
      await loading.dismiss();
      this.messageService.showError(error);
    }
  }

  getExpandButtonText(insight: KnowledgeGapInsight, action: InsightAction, isExpanded: boolean): string {
    const count = this.getTopicsForAction(insight, action).length;
    if (isExpanded) {
      return 'Hide related conversations';
    }
    if (count === 1) {
      return 'Show 1 related conversation';
    }
    return `Show ${count} related conversations`;
  }
}
